import { useEffect, useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { EmptyBlock, ErrorBlock, Panel, Spinner } from '~/components/ui'
import { fetchMyOrders, OrderState, TradeOrder } from '../api/trade'

type Role = 'purchases' | 'sales'

/** Order state in the words a player would use.
 *
 *  Paid and Delivering are one thing from the outside, "on its way". Needs
 *  attention is shown as a delay rather than a fault: staff are already looking
 *  at it, and the buyer's money is not at risk. */
const STATE_TEXT: Record<number, { text: string; tone: string }> = {
    [OrderState.PAID]: { text: 'On its way', tone: 'text-[#03CEFF]' },
    [OrderState.DELIVERING]: { text: 'On its way', tone: 'text-[#03CEFF]' },
    [OrderState.COMPLETE]: { text: 'Delivered', tone: 'text-[#00FF90]' },
    [OrderState.NEEDS_ATTENTION]: {
        text: 'Delayed, our team is looking at it',
        tone: 'text-[#FFC53D]',
    },
    [OrderState.FAILED]: { text: 'Refunded', tone: 'text-white/60' },
}

const describe = (order: TradeOrder) =>
    STATE_TEXT[order.state] ?? { text: order.state_label, tone: 'text-white/60' }

export const OrderHistory = () => {
    const [role, setRole] = useState<Role>('purchases')
    const [orders, setOrders] = useState<TradeOrder[] | null>(null)
    const [loadError, setLoadError] = useState(false)

    const load = () => {
        setLoadError(false)
        setOrders(null)
        fetchMyOrders(role)
            .then((data) => setOrders(data.results))
            .catch(() => setLoadError(true))
    }

    useEffect(load, [role])

    return (
        <div className='flex flex-col gap-4'>
            <div className='flex gap-2'>
                {(['purchases', 'sales'] as Role[]).map((entry) => (
                    <button
                        key={entry}
                        onClick={() => setRole(entry)}
                        className={twMerge(
                            'border px-3 py-1.5 text-[11px] font-black uppercase tracking-widest transition-colors',
                            role === entry
                                ? 'border-[#03CEFF] text-white'
                                : 'border-white/10 text-white/50 hover:border-white/25'
                        )}
                    >
                        {entry === 'purchases' ? 'Purchases' : 'Sales'}
                    </button>
                ))}
            </div>

            {loadError && <ErrorBlock onRetry={load} />}
            {!loadError && orders === null && (
                <div className='flex justify-center py-16'>
                    <Spinner visible={true} />
                </div>
            )}
            {!loadError && orders?.length === 0 && (
                <EmptyBlock title={role === 'purchases' ? 'No purchases yet' : 'No sales yet'}>
                    {role === 'purchases'
                        ? 'NFTs you buy here will show up with their delivery status.'
                        : 'When one of your listings sells it will show up here.'}
                </EmptyBlock>
            )}

            {!loadError &&
                orders?.map((order) => {
                    const { text, tone } = describe(order)
                    return (
                        <Panel key={order.id} className='flex flex-col gap-3 p-3 sm:flex-row sm:items-center'>
                            <div className='h-16 w-16 shrink-0 overflow-hidden bg-white/[0.03]'>
                                {order.nft.image_url && (
                                    <img
                                        src={order.nft.image_url}
                                        alt={order.nft.name}
                                        className='h-full w-full object-contain'
                                    />
                                )}
                            </div>
                            <div className='min-w-0 flex-1'>
                                <p className='truncate text-sm font-bold text-white'>{order.nft.name}</p>
                                <p className='text-[11px] uppercase tracking-widest text-white/40'>
                                    {new Date(order.created_at).toLocaleDateString()} · ${order.price}
                                    {/* A refunded sale paid the seller nothing, whatever the row still says. */}
                                    {role === 'sales' &&
                                        order.state !== OrderState.FAILED &&
                                        ` · you receive $${order.proceeds}`}
                                </p>
                                <p className={twMerge('mt-1 text-xs font-bold', tone)}>{text}</p>
                                {order.state === OrderState.COMPLETE && order.delivery_signature && (
                                    <a
                                        href={`https://solscan.io/tx/${order.delivery_signature}`}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className='text-xs text-[#03CEFF] underline'
                                    >
                                        View on Solscan
                                    </a>
                                )}
                            </div>
                        </Panel>
                    )
                })}
        </div>
    )
}
